import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, RefreshCw, AlertTriangle, Tags, Store } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtMoney, fmtNumber } from '../components/Expenses/format';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

/**
 * ExpenseMappings — the reference data behind Expense Analysis, managed here
 * rather than by re-uploading a spreadsheet.
 *
 * Deliberately separate from the monthly transaction import: the item->category
 * mapping and the merchant rules are slow-moving reference data that restate
 * ALL history when edited, whereas the transaction feed is append-only.
 */
export default function ExpenseMappings() {
  const [tab, setTab] = useState('items');
  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Expense Mappings"
        subtitle="Reference data for Expense Analysis. Edits here restate every month of history at once — they do not touch the imported transactions themselves."
      />

      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(tab === 'items' ? s.tabActive : {}) }} onClick={() => setTab('items')}>
          <Tags size={13} /> Expense Item → Spend Category
        </button>
        <button style={{ ...s.tab, ...(tab === 'merchants' ? s.tabActive : {}) }} onClick={() => setTab('merchants')}>
          <Store size={13} /> Merchant Normalization
        </button>
      </div>

      {tab === 'items' ? <ItemMap /> : <MerchantMap />}
    </div>
  );
}

// ── Expense item -> spend category ──────────────────────────────────────────
function ItemMap() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [form, setForm] = useState({ expense_item: '', spend_category: '', expense_item_group: '', reference_id: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['expense-item-map'],
    queryFn:  () => api.get('/expenses/item-map'),
  });

  const save = useMutation({
    mutationFn: (body) => api.post('/expenses/item-map', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expense-item-map'] }); setForm({ expense_item: '', spend_category: '', expense_item_group: '', reference_id: '' }); },
    onError: (e) => alert(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/expenses/item-map/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense-item-map'] }),
    onError: (e) => alert(e.message),
  });

  if (isLoading) return <LoadingSpinner />;

  const items    = data?.items || [];
  const unmapped = data?.unmapped || [];

  return (
    <>
      {unmapped.length > 0 && (
        <div style={s.warnBanner}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <div>
            <strong>{unmapped.length} expense item{unmapped.length !== 1 ? 's' : ''} with no spend category.</strong>{' '}
            {fmtNumber(unmapped.reduce((a, u) => a + u.transaction_count, 0))} transactions worth{' '}
            {fmtMoney(unmapped.reduce((a, u) => a + u.net_amount, 0))} currently group under
            &lt;Unmapped&gt; on the dashboard — they are still counted in every total, never dropped.
            <div style={{ marginTop: 6 }}>
              {unmapped.map((u) => (
                <button key={u.expense_item} style={s.chip}
                        onClick={() => setForm((f) => ({ ...f, expense_item: u.expense_item }))}>
                  {u.expense_item} · {fmtMoney(u.net_amount)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isSuper && (
        <div style={s.card}>
          <div style={s.cardTitle}>Add or update a mapping</div>
          <div style={s.formRow}>
            <Field label="Expense Item" value={form.expense_item} width={230}
                   onChange={(v) => setForm((f) => ({ ...f, expense_item: v }))} />
            <Field label="Spend Category" value={form.spend_category} width={210}
                   onChange={(v) => setForm((f) => ({ ...f, spend_category: v }))} />
            <Field label="Item Group" value={form.expense_item_group} width={160}
                   onChange={(v) => setForm((f) => ({ ...f, expense_item_group: v }))} />
            <Field label="Reference ID" value={form.reference_id} width={140}
                   onChange={(v) => setForm((f) => ({ ...f, reference_id: v }))} />
            <button style={s.primaryBtn} disabled={!form.expense_item.trim() || save.isPending}
                    onClick={() => save.mutate(form)}>
              <Plus size={13} /> Save
            </button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardTitle}>{items.length} mappings · {new Set(items.map((i) => i.spend_category)).size} spend categories</div>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Expense Item</th><th style={s.th}>Spend Category</th>
              <th style={s.th}>Group</th><th style={s.th}>Ref ID</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Transactions</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
              {isSuper && <th style={s.th} />}
            </tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>{r.expense_item}</td>
                  <td style={s.td}>
                    {r.spend_category || <span style={s.missing}>— none —</span>}
                  </td>
                  <td style={s.td}>{r.expense_item_group || '—'}</td>
                  <td style={{ ...s.td, color: '#9CA3AF', fontSize: '10.5px' }}>{r.reference_id || '—'}</td>
                  <td style={s.tdNum}>{fmtNumber(r.transaction_count)}</td>
                  <td style={s.tdNum}>{fmtMoney(r.net_amount)}</td>
                  {isSuper && (
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      <button style={s.iconBtn} title="Delete mapping"
                              onClick={() => confirm(`Delete the mapping for "${r.expense_item}"? Its transactions will move to <Unmapped>.`) && del.mutate(r.id)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Merchant normalization ──────────────────────────────────────────────────
function MerchantMap() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [form, setForm] = useState({ match_type: 'prefix', pattern: '', canonical_merchant: '', priority: 100 });

  const { data, isLoading } = useQuery({
    queryKey: ['expense-merchant-map'],
    queryFn:  () => api.get('/expenses/merchant-map'),
  });

  const save = useMutation({
    mutationFn: (body) => api.post('/expenses/merchant-map', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expense-merchant-map'] }); setForm({ match_type: 'prefix', pattern: '', canonical_merchant: '', priority: 100 }); },
    onError: (e) => alert(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/expenses/merchant-map/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense-merchant-map'] }),
    onError: (e) => alert(e.message),
  });
  const reapply = useMutation({
    mutationFn: () => api.post('/expenses/merchant-map/reapply'),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['expense-merchant-map'] });
      alert(`Rules reapplied across ${fmtNumber(r.distinctMerchants)} distinct merchants — ${fmtNumber(r.rowsUpdated)} transactions updated.`);
    },
    onError: (e) => alert(e.message),
  });

  if (isLoading) return <LoadingSpinner />;

  const rules = data?.rules || [];
  const un    = data?.unnormalized || [];

  return (
    <>
      <div style={s.infoBanner}>
        Payment processors append transaction ids to merchant names, so the raw field is unusable as a
        filter on its own. Rules collapse those variants into one name. They are applied when a file is
        imported — after editing rules, use <strong>Reapply to all history</strong> to restate existing
        transactions. That step is manual on purpose so a rule edit never silently changes numbers
        someone is part-way through reviewing.
      </div>

      {isSuper && (
        <div style={s.card}>
          <div style={s.cardTitle}>Add or update a rule</div>
          <div style={s.formRow}>
            <div style={{ ...s.field, width: 120 }}>
              <label style={s.label}>Match Type</label>
              <select style={s.input} value={form.match_type}
                      onChange={(e) => setForm((f) => ({ ...f, match_type: e.target.value }))}>
                <option value="prefix">Prefix</option>
                <option value="exact">Exact</option>
                <option value="contains">Contains</option>
                <option value="regex">Regex</option>
              </select>
            </div>
            <Field label="Pattern" value={form.pattern} width={240}
                   onChange={(v) => setForm((f) => ({ ...f, pattern: v }))} placeholder="AMAZON MKTPL" />
            <Field label="Canonical Merchant" value={form.canonical_merchant} width={200}
                   onChange={(v) => setForm((f) => ({ ...f, canonical_merchant: v }))} placeholder="Amazon" />
            <Field label="Priority" value={form.priority} width={90} type="number"
                   onChange={(v) => setForm((f) => ({ ...f, priority: Number(v) || 100 }))} />
            <button style={s.primaryBtn}
                    disabled={!form.pattern.trim() || !form.canonical_merchant.trim() || save.isPending}
                    onClick={() => save.mutate(form)}>
              <Plus size={13} /> Save
            </button>
            <button style={s.secondaryBtn} disabled={reapply.isPending} onClick={() => reapply.mutate()}>
              <RefreshCw size={13} /> {reapply.isPending ? 'Reapplying…' : 'Reapply to all history'}
            </button>
          </div>
        </div>
      )}

      <div style={s.twoCol}>
        <div style={s.card}>
          <div style={s.cardTitle}>{rules.length} rules · lowest priority wins</div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Type</th><th style={s.th}>Pattern</th>
                <th style={s.th}>Canonical</th><th style={{ ...s.th, textAlign: 'right' }}>Pri</th>
                {isSuper && <th style={s.th} />}
              </tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} style={s.tr}>
                    <td style={{ ...s.td, color: '#9CA3AF' }}>{r.match_type}</td>
                    <td style={{ ...s.td, fontFamily: 'ui-monospace, monospace', fontSize: '10.5px' }}>{r.pattern}</td>
                    <td style={{ ...s.td, fontWeight: 500 }}>{r.canonical_merchant}</td>
                    <td style={s.tdNum}>{r.priority}</td>
                    {isSuper && (
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <button style={s.iconBtn} onClick={() => confirm(`Delete rule "${r.pattern}"?`) && del.mutate(r.id)}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>Top unnormalized merchants</div>
          <div style={s.cardSub}>
            Raw names no rule has collapsed, ranked by spend. These are the highest-value rules to add next.
          </div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Raw Merchant</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Txns</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                {isSuper && <th style={s.th} />}
              </tr></thead>
              <tbody>
                {un.slice(0, 60).map((r) => (
                  <tr key={r.merchant_raw} style={s.tr}>
                    <td style={{ ...s.td, fontFamily: 'ui-monospace, monospace', fontSize: '10.5px' }}>{r.merchant_raw}</td>
                    <td style={s.tdNum}>{fmtNumber(r.n)}</td>
                    <td style={s.tdNum}>{fmtMoney(r.net_amount)}</td>
                    {isSuper && (
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <button style={s.linkBtn}
                                onClick={() => setForm({ match_type: 'prefix', pattern: r.merchant_raw.slice(0, 14),
                                                          canonical_merchant: '', priority: 100 })}>
                          rule
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, value, onChange, width = 180, placeholder, type = 'text' }) {
  return (
    <div style={{ ...s.field, width }}>
      <label style={s.label}>{label}</label>
      <input style={s.input} value={value} type={type} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const s = {
  page:  { maxWidth: '1500px', margin: '0 auto' },
  tabs:  { display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #E5E7EB' },
  tab: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', background: 'none', border: 'none', borderBottom: '2px solid transparent',
    fontSize: '12.5px', color: '#6B7280', cursor: 'pointer', marginBottom: '-1px',
  },
  tabActive: { color: '#1B2A4A', fontWeight: 600, borderBottomColor: '#1B2A4A' },
  card:      { ...card.sm, padding: '14px 16px', marginBottom: '14px' },
  cardTitle: { fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '10px' },
  cardSub:   { fontSize: '11px', color: '#9CA3AF', marginTop: '-6px', marginBottom: '10px', lineHeight: 1.5 },
  twoCol:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '14px', alignItems: 'start' },
  formRow:   { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  field:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:     { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280' },
  input:     { height: '32px', padding: '0 8px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '12px', width: '100%', fontFamily: 'inherit' },
  primaryBtn:{ ...btn.primarySm },
  secondaryBtn: { ...btn.secondarySm },
  warnBanner:{ display: 'flex', gap: '10px', padding: '12px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', fontSize: '12px', color: '#92400E', marginBottom: '14px', lineHeight: 1.55 },
  infoBanner:{ padding: '12px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', fontSize: '12px', color: '#1E40AF', marginBottom: '14px', lineHeight: 1.55 },
  chip:      { display: 'inline-block', margin: '3px 5px 0 0', padding: '2px 8px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '10px', fontSize: '10.5px', color: '#92400E', cursor: 'pointer' },
  scroll:    { overflowX: 'auto', maxHeight: '560px', overflowY: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th: { padding: '7px 9px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB',
        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#6B7280',
        whiteSpace: 'nowrap', position: 'sticky', top: 0 },
  tr:    { ...tbl.tr, ...tbl.tr },
  td:    { ...tbl.td, ...tbl.tdSm },
  tdNum: { ...tbl.tdR, ...tbl.tdRSm },
  missing:{ color: '#B45309', fontStyle: 'italic' },
  iconBtn:{ ...btn.icon },
  linkBtn: { ...btn.linkXs },
};
